import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Facebook,
  Instagram,
  Youtube,
  ArrowRight,
} from "lucide-react";
import toast from "react-hot-toast";
import { FaTiktok } from "react-icons/fa";

import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";

export default function ContactPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const handleSearch = () => {
    navigate("/search");
  };

  const handleAddListing = () => {
    // Not logged in
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent("/add-listing")}`);
      return;
    }

    // Only OWNER or ADMIN
    if (!["OWNER", "ADMIN"].includes(user.role)) {
      toast.error("Only Property Owners and Administrators can list properties.");
      return;
    }

    navigate("/add-listing");
  };

  return (
    <div className="bg-white">
      {/* ================================================= */}
      {/* HERO */}
      {/* ================================================= */}

      <section className="relative overflow-hidden bg-gradient-to-r from-orange-600 via-orange-500 to-red-500">
        {/* Background */}

        <div className="absolute inset-0">
          <div className="absolute -top-40 -left-32 w-[450px] h-[450px] rounded-full bg-white/10 blur-3xl"></div>

          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-yellow-300/10 blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-8">
          <div className="max-w-3xl">
            <span className="inline-block px-5 py-2 rounded-full bg-white/20 text-white font-semibold">
              Contact RoomVetiyo
            </span>

            <h1 className="text-5xl lg:text-6xl font-extrabold text-white mt-6 leading-tight">
              We'd Love
              <span className="block text-yellow-300">To Hear From You</span>
            </h1>

            <p className="mt-8 text-xl text-orange-100 leading-7">
              Have a question, feedback, partnership proposal, or need
              assistance? Our team is always ready to help you find the perfect
              rental experience.
            </p>

            <div className="flex items-center gap-3 mt-6 text-white">
              <a href="/" className="hover:text-yellow-300">
                Home
              </a>

              <span>/</span>

              <span className="text-yellow-300">Contact Us</span>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* CONNECT WITH US */}
      {/* ================================================= */}

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* LEFT */}

            <div>
              <span className="text-orange-600 uppercase tracking-widest font-semibold">
                Connect With Us
              </span>

              <h2 className="text-5xl font-bold mt-4">Stay Connected</h2>

              <p className="mt-8 text-lg text-gray-600 leading-7">
                Follow RoomVetiyo on social media to stay updated with the
                latest rental listings, platform updates and housing tips.
              </p>

              <div className="flex flex-wrap gap-5 mt-10">
                <a
                  href="https://facebook.com/roomvetiyodotcom"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="w-16 h-16 rounded-2xl bg-[#1877F2] text-white flex items-center justify-center hover:scale-110 transition duration-300"
                >
                  <Facebook size={28} />
                </a>

                <a
                  href="https://instagram.com/roomvetiyo"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 text-white flex items-center justify-center hover:scale-110 transition duration-300"
                >
                  <Instagram size={28} />
                </a>

                <a
                  href="https://www.tiktok.com/@roomvetiyo"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  className="w-16 h-16 rounded-2xl bg-black text-white flex items-center justify-center hover:scale-110 transition duration-300"
                >
                  <FaTiktok size={28} />
                </a>

                <a
                  href="https://www.youtube.com/@RoomVetiyo"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  className="w-16 h-16 rounded-2xl bg-red-600 text-white flex items-center justify-center hover:scale-110 transition duration-300"
                >
                  <Youtube size={28} />
                </a>
              </div>
            </div>

            {/* RIGHT */}

            <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-[35px] p-10 text-white">
              <h3 className="text-4xl font-bold">Need Immediate Help?</h3>

              <p className="mt-6 leading-9 text-orange-100">
                Our support team is committed to responding to your questions as
                quickly as possible. We typically reply within one business day.
              </p>

              {/* <button className="mt-10 bg-white text-orange-600 px-8 py-4 rounded-2xl font-semibold flex items-center gap-3 hover:scale-105 transition">
                Contact Support
                <ArrowRight size={20} />
              </button> */}
            </div>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* CONTACT FORM */}
      {/* ================================================= */}

      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <span className="text-orange-600 uppercase tracking-widest font-semibold">
              Send Message
            </span>

            <h2 className="text-5xl font-bold mt-3">
              We'd Love To Hear From You
            </h2>

            <p className="text-gray-600 mt-6 max-w-3xl mx-auto leading-7">
              Fill out the form below and our team will get back to you as soon
              as possible.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 mt-6">
            {/* ================================================= */}
            {/* CONTACT FORM */}
            {/* ================================================= */}

            <div className="bg-white rounded-[35px] px-10 py-3 shadow-xl">
              <h3 className="text-3xl font-bold">Send Us a Message</h3>

              <p className="text-gray-500 mt-2 leading-7">
                Complete the form below and we'll respond as quickly as
                possible.
              </p>

              <form className="mt-4 space-y-3">
                {/* Name */}

                <div>
                  <label className="block font-semibold mb-3">Full Name</label>

                  <input
                    type="text"
                    placeholder="Enter your full name"
                    className="w-full border border-gray-300 rounded-2xl px-5 py-3 focus:ring-2 focus:ring-orange-500 outline-none transition"
                  />
                </div>

                {/* Email */}

                <div>
                  <label className="block font-semibold mb-3">
                    Email Address
                  </label>

                  <input
                    type="email"
                    placeholder="you@example.com"
                    className="w-full border border-gray-300 rounded-2xl px-5 py-3 focus:ring-2 focus:ring-orange-500 outline-none transition"
                  />
                </div>

                {/* Phone */}

                <div>
                  <label className="block font-semibold mb-3">
                    Phone Number
                  </label>

                  <input
                    type="tel"
                    placeholder="+977 98XXXXXXXX"
                    className="w-full border border-gray-300 rounded-2xl px-5 py-3 focus:ring-2 focus:ring-orange-500 outline-none transition"
                  />
                </div>

                {/* Subject */}

                <div>
                  <label className="block font-semibold mb-3">Subject</label>

                  <input
                    type="text"
                    placeholder="Subject"
                    className="w-full border border-gray-300 rounded-2xl px-5 py-3 focus:ring-2 focus:ring-orange-500 outline-none transition"
                  />
                </div>

                {/* Inquiry Type */}

                <div>
                  <label className="block font-semibold mb-3">
                    Inquiry Type
                  </label>

                  <select className="w-full border border-gray-300 rounded-2xl px-5 py-3 focus:ring-2 focus:ring-orange-500 outline-none transition">
                    <option>General Inquiry</option>

                    <option>Property Listing</option>

                    <option>Technical Support</option>

                    <option>Report a Listing</option>

                    <option>Partnership</option>

                    <option>Feedback</option>
                  </select>
                </div>

                {/* Message */}

                <div>
                  <label className="block font-semibold mb-3">Message</label>

                  <textarea
                    rows={6}
                    placeholder="Write your message..."
                    className="w-full border border-gray-300 rounded-2xl px-5 py-3 resize-none focus:ring-2 focus:ring-orange-500 outline-none transition"
                  ></textarea>
                </div>

                {/* Privacy */}

                <div className="flex gap-3 items-start">
                  <input
                    type="checkbox"
                    className="mt-1 w-5 h-5 accent-orange-500"
                  />

                  <p className="text-gray-600 leading-7">
                    I agree to the Privacy Policy and consent to RoomVetiyo
                    storing my information for responding to this inquiry.
                  </p>
                </div>

                {/* Button */}

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-600 to-red-500 text-white py-3 rounded-2xl text-lg font-semibold hover:scale-[1.02] transition duration-300 shadow-lg"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* ================================================= */}
            {/* RIGHT SIDE */}
            {/* ================================================= */}

            <div className="space-y-4">
              {/* Card */}

              <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-[35px] px-10 py-5 text-white">
                <h3 className="text-4xl font-bold">Why Contact Us?</h3>

                <p className="mt-4 text-orange-100 leading-7">
                  Whether you are looking for a room, facing an issue, reporting
                  a listing, or interested in partnering with us, our dedicated
                  team is ready to assist you.
                </p>
              </div>

              {/* Response Time */}

              <div className="bg-white rounded-[35px] px-8 py-5 shadow-lg">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center text-3xl">
                    ⚡
                  </div>

                  <div>
                    <h4 className="text-xl font-bold">Fast Response</h4>

                    <p className="text-gray-500 mt-2">
                      Average reply within one business day.
                    </p>
                  </div>
                </div>
              </div>

              {/* Support */}

              <div className="bg-white rounded-[35px] px-8 py-5 shadow-lg">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center text-3xl">
                    💬
                  </div>

                  <div>
                    <h4 className="text-xl font-bold">Friendly Support</h4>

                    <p className="text-gray-500 mt-2">
                      Our support team is always happy to help.
                    </p>
                  </div>
                </div>
              </div>

              {/* Secure */}

              <div className="bg-white rounded-[35px] px-8 py-5 shadow-lg">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center text-3xl">
                    🔒
                  </div>

                  <div>
                    <h4 className="text-xl font-bold">
                      Your Information Is Safe
                    </h4>

                    <p className="text-gray-500 mt-2">
                      We respect your privacy and never share your personal
                      information without permission.
                    </p>
                  </div>
                </div>
              </div>

              {/* Illustration */}

              <div className="rounded-[35px] overflow-hidden shadow-xl">
                <img
                  src="/images/contact/contact-support.jpg"
                  alt="Customer Support"
                  className="w-full h-[360px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* GOOGLE MAP + OFFICE DETAILS */}
      {/* ================================================= */}

      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-orange-600 uppercase tracking-widest font-semibold">
              Visit Our Office
            </span>

            <h2 className="text-5xl font-bold mt-3">Find Us Easily</h2>

            <p className="mt-4 text-lg text-gray-600 leading-7">
              We'd love to meet you in person. Visit our office during business
              hours or reach out through any of our online communication
              channels.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-10 mt-8">
            {/* ================================================= */}
            {/* GOOGLE MAP */}
            {/* ================================================= */}

            <div className="lg:col-span-2">
              <div className="overflow-hidden rounded-[35px] shadow-xl h-[600px]">
                <iframe
                  title="RoomVetiyo Location"
                  src="https://www.google.com/maps?q=Kathmandu,Nepal&output=embed"
                  className="w-full h-full border-0"
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* ================================================= */}
            {/* OFFICE INFO */}
            {/* ================================================= */}

            <div className="space-y-5">
              <div className="bg-orange-50 rounded-[30px] px-8 py-4">
                <h3 className="text-2xl font-bold">Office Address</h3>

                <p className="mt-3 text-gray-600 leading-7">
                  RoomVetiyo Pvt. Ltd.
                  <br />
                  Kathmandu Metropolitan City
                  <br />
                  Kathmandu, Nepal
                </p>
              </div>

              <div className="bg-blue-50 rounded-[30px] px-8 py-4">
                <h3 className="text-2xl font-bold">Contact Information</h3>

                <div className="space-y-3 mt-3">
                  <div>
                    <p className="font-semibold">Phone</p>

                    <p className="text-gray-600">+977-9708919809</p>
                  </div>

                  <div>
                    <p className="font-semibold">Email</p>

                    <p className="text-gray-600 break-all">
                      support@roomvetiyo.com
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">Website</p>

                    <p className="text-gray-600">www.roomvetiyo.com</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-[30px] px-8 py-4">
                <h3 className="text-2xl font-bold">Office Hours</h3>

                <div className="space-y-3 mt-3">
                  <div className="flex justify-between">
                    <span>Sunday - Friday</span>

                    <span className="font-semibold">9:00 AM - 6:00 PM</span>
                  </div>

                  <div className="flex justify-between">
                    <span>Saturday</span>

                    <span className="font-semibold text-red-500">Closed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* SERVICE AREAS */}
      {/* ================================================= */}

      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <span className="text-orange-600 uppercase tracking-widest font-semibold">
              Service Areas
            </span>

            <h2 className="text-5xl font-bold mt-3">
              Helping Renters Across Nepal
            </h2>

            <p className="text-gray-600 mt-4 max-w-3xl mx-auto leading-7">
              RoomVetiyo is expanding throughout Nepal to make finding rental
              properties easier and more reliable.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-6">
            {[
              "Kathmandu",
              "Lalitpur",
              "Bhaktapur",
              "Pokhara",
              "Biratnagar",
              "Butwal",
              "Chitwan",
              "Dharan",
            ].map((city, index) => (
              <div
                key={index}
                className="bg-white rounded-[25px] px-8 py-4 shadow hover:shadow-xl transition hover:-translate-y-2"
              >
                <div className="text-4xl">📍</div>

                <h3 className="text-xl font-bold mt-3">{city}</h3>

                <p className="text-gray-500 mt-2">Rental listings available.</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* TESTIMONIALS */}
      {/* ================================================= */}

      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <span className="text-orange-600 uppercase tracking-widest font-semibold">
              Testimonials
            </span>

            <h2 className="text-5xl font-bold mt-3">What Our Users Say</h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mt-6">
            {[
              {
                name: "Aarav Sharma",
                role: "Tenant",
                review:
                  "RoomVetiyo helped me find a room within two days. The platform was simple and the owner responded quickly.",
              },
              {
                name: "Sita Gurung",
                role: "Property Owner",
                review:
                  "Listing my apartment was very easy and I received genuine inquiries from interested tenants.",
              },
              {
                name: "Ramesh Karki",
                role: "Business Owner",
                review:
                  "I found an office space through RoomVetiyo without the hassle of visiting dozens of locations.",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-[30px] px-8 py-4 shadow-sm hover:shadow-xl transition"
              >
                <div className="text-yellow-500 text-2xl">★★★★★</div>

                <p className="mt-4 text-gray-600 leading-7">"{item.review}"</p>

                <div className="mt-4">
                  <h4 className="font-bold text-xl">{item.name}</h4>

                  <p className="text-orange-600 mt-2">{item.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* FAQ */}
      {/* ================================================= */}

      <section className="py-8 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <span className="text-orange-600 uppercase tracking-widest font-semibold">
              Frequently Asked Questions
            </span>

            <h2 className="text-5xl font-bold mt-3">
              Everything You Need To Know
            </h2>

            <p className="mt-4 text-lg text-gray-600 leading-7">
              Here are some of the most common questions our users ask.
            </p>
          </div>

          <div className="mt-6 space-y-4">
            {[
              {
                q: "How quickly will I receive a response?",
                a: "Our support team usually responds within one business day. For urgent issues, please contact us by phone.",
              },
              {
                q: "Can I report a fake or misleading listing?",
                a: "Yes. You can report any listing directly from the property page or contact our support team.",
              },
              {
                q: "Is creating a RoomVetiyo account free?",
                a: "Yes. Creating an account and browsing listings is completely free.",
              },
              {
                q: "Can I update my property listing after publishing it?",
                a: "Absolutely. Property owners can edit, update, or remove listings anytime from their dashboard.",
              },
              {
                q: "Do you verify property listings?",
                a: "We encourage verification and review reported listings to help maintain a trustworthy marketplace.",
              },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-[30px] px-8 py-4 shadow-sm hover:shadow-lg transition"
              >
                <h3 className="text-2xl font-bold">{faq.q}</h3>

                <p className="mt-3 text-gray-600 leading-7">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================= */}
      {/* CALL TO ACTION */}
      {/* ================================================= */}

      <section className="relative overflow-hidden bg-gradient-to-r from-orange-600 via-orange-500 to-red-500 py-8">
        <div className="absolute inset-0">
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl"></div>

          <div className="absolute bottom-0 right-0 w-[450px] h-[450px] rounded-full bg-yellow-300/10 blur-3xl"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <span className="inline-block px-5 py-2 rounded-full bg-white/20 text-white font-semibold">
            Need More Assistance?
          </span>

          <h2 className="text-5xl lg:text-6xl font-bold text-white mt-4">
            Let's Make Your Rental
            <span className="block text-yellow-300 mt-2">Journey Easier</span>
          </h2>

          <p className="mt-4 text-xl text-orange-100 leading-7">
            Whether you're searching for a room, listing a property, or simply
            have questions, our team is here to help every step of the way.
          </p>

          <div className="flex flex-wrap justify-center gap-6 mt-5">
            <button
              onClick={handleSearch}
              className="px-10 py-4 bg-white text-orange-600 rounded-2xl font-bold hover:scale-105 transition"
            >
              Explore Properties
            </button>

            <button
              onClick={handleAddListing}
              className="px-10 py-4 border-2 border-white text-white rounded-2xl font-bold hover:bg-white hover:text-orange-600 transition"
            >
              List Your Property
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
